import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import AdminApiService from '../services/api.service';
import { useAuth } from './AuthContext';

// Create the business context
const BusinessContext = createContext(null);

export function BusinessProvider({ children }) {
  const { user, isPlatformAdmin, isAuthenticated } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Add a ref to track if we've attempted to fetch businesses
  const apiCallAttempted = useRef(false);

  // Define selectBusiness function first to avoid reference error
  const selectBusiness = useCallback(async (businessId) => {
    try {
      const business = businesses.find(b => b.id === businessId);
      
      if (!business) {
        console.error(`Business with ID ${businessId} not found`);
        throw new Error('Business not found');
      }
      
      // Set the business in context and localStorage for API calls
      setSelectedBusiness(business);
      localStorage.setItem('selectedBusinessId', businessId);
      AdminApiService.businesses.setActive(businessId);
      
      return business;
    } catch (error) {
      console.error('Failed to select business:', error);
      setError(`Failed to select business: ${error.message}`);
      throw error;
    }
  }, [businesses]);

  // Fetch all businesses accessible to the current user
  const fetchBusinesses = useCallback(async () => {
    // If we've already tried fetching, don't try again automatically
    if (apiCallAttempted.current) {
      console.log('Skipping automatic business fetch - already attempted');
      return [];
    }
    
    try {
      // Mark that we've attempted an API call
      apiCallAttempted.current = true;
      
      setLoading(true);
      setError(null);
      
      console.log('Fetching businesses');
      
      // If platform admin, fetch all businesses. Otherwise, fetch only accessible businesses
      const data = await AdminApiService.businesses.getAll();
      
      // Handle the case where API returns null or undefined
      if (data === null || data === undefined) {
        console.warn('Business API returned null or undefined, treating as empty array');
        setBusinesses([]);
        return [];
      }
      
      // Ensure data is always treated as an array
      const businessArray = Array.isArray(data) ? data : [];
      setBusinesses(businessArray);
      
      return businessArray;
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
      setError('Failed to load businesses. Please refresh the page to try again.');
      setBusinesses([]); // Set to empty array on error to prevent undefined issues
      return [];
    } finally {
      setLoading(false);
    }
  }, []); // Keep the empty dependency array
  
  // Function to manually retry fetching businesses (for refresh button)
  const manualRetryFetchBusinesses = useCallback(() => {
    // Reset the API call attempted flag
    apiCallAttempted.current = false;
    
    // Clear any existing error
    setError(null);
    
    // Reload the page - simplest way to ensure a clean refresh
    window.location.reload();
    
    // Return a promise to maintain API compatibility
    return Promise.resolve([]);
  }, []);

  // Load businesses when user auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      // Only attempt to fetch if we haven't already tried
      if (!apiCallAttempted.current) {
        fetchBusinesses().catch(err => {
          console.error('Error in initial business fetch:', err);
          setError('Failed to load businesses. Please refresh the page to try again.');
          // Ensure we don't retry automatically
          apiCallAttempted.current = true;
        });
      }
    }
  }, [isAuthenticated, user, fetchBusinesses]);

  // Auto-selection of businesses is disabled
  // Users will explicitly select a business from the Businesses page
  // This improves navigation flow and allows platform admins to see aggregate statistics first

  // Check for selected business in localStorage
  useEffect(() => {
    // Get the current path
    const path = window.location.pathname;
    
    // For platform admins on the dashboard page, don't select a business
    if (isPlatformAdmin && path === '/dashboard') {
      console.log('Platform admin on dashboard, clearing business selection');
      localStorage.removeItem('selectedBusinessId');
      setSelectedBusiness(null);
      return;
    }
    
    // For other pages, load the selected business if available
    const selectedBusinessId = localStorage.getItem('selectedBusinessId');
    
    if (selectedBusinessId && businesses.length > 0) {
      const business = businesses.find(b => b.id === selectedBusinessId);
      if (business) {
        setSelectedBusiness(business);
      } else {
        // Clear invalid business ID from localStorage
        localStorage.removeItem('selectedBusinessId');
        // Clear the selected business state
        setSelectedBusiness(null);
        
        // For platform admins, we don't auto-select any business
        // Users will need to explicitly select a business from the Businesses page
        console.log('Invalid business ID in localStorage, cleared selection');
      }
    }
  }, [businesses, loading, isPlatformAdmin]);

  // Create a new business
  const createBusiness = async (businessData) => {
    try {
      setLoading(true);
      
      const newBusiness = await AdminApiService.businesses.create(businessData);
      
      // Reset API call tracking on successful operation
      apiCallAttempted.current = false;
      
      // Refresh the business list
      await fetchBusinesses();
      
      // Select the newly created business
      await selectBusiness(newBusiness.id);
      
      return newBusiness;
    } catch (error) {
      console.error('Failed to create business:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing business
  const updateBusiness = async (businessId, businessData) => {
    try {
      setLoading(true);
      
      // Create a merged business object based on existing data and updates
      // This ensures we have a complete object even if the API call fails
      const mergedBusiness = selectedBusiness && selectedBusiness.id === businessId
        ? { ...selectedBusiness, ...businessData, updated: Date.now() }
        : { ...businessData, id: businessId, updated: Date.now() };
      
      console.log('Sending update request with data:', businessData);
      
      try {
        // Try to update via API
        const updatedBusiness = await AdminApiService.businesses.update(businessId, businessData);
        console.log('API update successful:', updatedBusiness);
        
        // Update the businesses list with the response from the API
        setBusinesses(prev => 
          prev.map(b => b.id === businessId ? updatedBusiness : b)
        );
        
        // Update selected business if it's the one being modified
        if (selectedBusiness && selectedBusiness.id === businessId) {
          setSelectedBusiness(updatedBusiness);
        }
        
        return updatedBusiness;
      } catch (apiError) {
        console.error('API update failed:', apiError);
        console.log('Using fallback local update with:', mergedBusiness);
        
        // If API update fails, still update the UI with the merged data
        // This provides a better user experience even if the backend fails
        setBusinesses(prev => 
          prev.map(b => b.id === businessId ? mergedBusiness : b)
        );
        
        // Update selected business if it's the one being modified
        if (selectedBusiness && selectedBusiness.id === businessId) {
          setSelectedBusiness(mergedBusiness);
        }
        
        // For subdomain changes, ensure we update localStorage to maintain state
        if (businessData.subdomain && selectedBusiness && selectedBusiness.id === businessId) {
          console.log(`Updating localStorage with new subdomain: ${businessData.subdomain}`);
          // We don't need to change the selected business ID, just ensure the business object has the new subdomain
        }
        
        // Re-throw the error for higher-level error handling
        throw apiError;
      }
    } catch (error) {
      console.error('Failed to update business:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a business
  const deleteBusiness = async (businessId) => {
    try {
      setLoading(true);
      
      await AdminApiService.businesses.delete(businessId);
      
      // Remove from the businesses list
      setBusinesses(prev => prev.filter(b => b.id !== businessId));
      
      // If the deleted business was selected, select another one
      if (selectedBusiness && selectedBusiness.id === businessId) {
        if (businesses.length > 1) {
          const nextBusiness = businesses.find(b => b.id !== businessId);
          await selectBusiness(nextBusiness.id);
        } else {
          setSelectedBusiness(null);
          AdminApiService.businesses.clearActive();
        }
      }
      
      return true;
    } catch (error) {
      console.error('Failed to delete business:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get business statistics
  const getBusinessStats = async (businessId = null) => {
    try {
      const targetId = businessId || (selectedBusiness ? selectedBusiness.id : null);
      
      if (!targetId) {
        throw new Error('No business selected');
      }
      
      return await AdminApiService.businesses.getStats(targetId);
    } catch (error) {
      console.error('Failed to get business stats:', error);
      throw error;
    }
  };

  // Context value - provide the manual retry function
  const value = {
    businesses,
    selectedBusiness,
    loading,
    error,
    selectBusiness,
    createBusiness,
    updateBusiness,
    deleteBusiness,
    refreshBusinesses: manualRetryFetchBusinesses, // Use the page reload function for simplicity
    getBusinessStats,
    canManageMultipleBusinesses: isPlatformAdmin
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

// Custom hook to use the business context
export function useBusiness() {
  const context = useContext(BusinessContext);
  if (context === null) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}